import {ITerritoryLayer} from "@esWebAPI/es/map/layer/territory/TerritoryLayer";
import {Map, source} from "azure-maps-control";
import {ILayerView, LayerInfo} from "@esWebAPI/es/model/Layer";
import {DatasourceManager} from "@esWebAPI/es/map/module/DatasourceManager";
import {IEventChannel} from "@esWebAPI/es/model/Models";
import {AzureImageSpriteManager} from "@esWebAPI/es/map/layer/AzureImageSpriteManager";
import {ThematicStylerFactory} from "@esWebAPI/es/map/styler/ThematicStylerFactory";
import {PointLayer} from "@esWebAPI/es/map/layer/PointLayer";
import TerritoryModel, {ITerritoryActions, ITerritoryFeature} from "@esWebAPI/es/map/TerritoryModel";
import {ExpressionFilterSpecification} from "@maplibre/maplibre-gl-style-spec";
import {TerritoryColorThematicStyler} from "@esWebAPI/es/map/styler/ColorThematicStyler";
import {ImageSpriteGenerator} from "@esWebAPI/es/map/layer/ImageSpriteGenerator";
import {isBubbleLayer} from "@esWebAPI/es/map/layer/azure/AzureLayer";

export class TerritoryPointLayer extends PointLayer implements ITerritoryLayer {

    private readonly _territoryModel: TerritoryModel;

    private readonly _spriteManager: AzureImageSpriteManager;
    private readonly _stylerFactory: ThematicStylerFactory;

    private _selectionLayer: PointLayer|undefined;
    private _selectionDs: source.Source|undefined;


    constructor(map: Map, layerView: ILayerView, layerInfo: LayerInfo,
                datasourceManager: DatasourceManager,
                spriteManager: AzureImageSpriteManager,
                stylerFactory: ThematicStylerFactory,
                territoryModel: TerritoryModel,
                eventChannel: IEventChannel) {
        super(map, layerView, layerInfo, datasourceManager, spriteManager, stylerFactory, eventChannel, undefined, undefined, new ImageSpriteGenerator(spriteManager, territoryModel));

        this._territoryModel = territoryModel;
        this._spriteManager = spriteManager;
        this._stylerFactory = stylerFactory;

        territoryModel.eventChannel.addListener("onTerritoryChange", this.onTerritoryChange, this);
        territoryModel.eventChannel.addListener("onTerritoryFilterChange", this.onTerritoryFilterChange, this);
        territoryModel.eventChannel.addListener("onTerritoryLevelChange", this.onTerritoryLevelChange, this);
        territoryModel.getSelectionModel().getEventChannel().addListener("onSelectionChange", this.onSelectionChange, this);

        this.createSelectionLayer();
    }

    private async createSelectionLayer() {
        const datasourceId = `es_${this.layerView.id}-selection`
        this._selectionDs = await this.datasourceManager.create(this.layerView, this.layerInfo, false, undefined, datasourceId)

        this._selectionLayer = new PointLayer(this.map, this.layerView, this.layerInfo,
            this.datasourceManager, this._spriteManager, this._stylerFactory, this.eventChannel,
            this._selectionDs, ['in', ['id'], ['literal', []]], new ImageSpriteGenerator(this._spriteManager, this.territoryModel));

        this._selectionLayer.sizeStyler.selection = true;
        (this._selectionLayer.colorStyler as TerritoryColorThematicStyler).selection = true;
    }

    private async onTerritoryChange(actions: ITerritoryActions, _territories: ITerritoryFeature[], _changeTerritoryIds: number[]) {
        if (actions.CHANGE_COLOR || actions.ADD_FEATS || actions.REMOVE_FEATS ||
            actions.ADD_EXCEPTIONS || actions.REMOVE_EXCEPTIONS ||
            actions.REMOVE_TERRITORY || actions.MOVE) {
            await this.update({});
        } else if (actions.RELOAD || actions.UNDO || actions.REDO) {
            await this.update({isForceReload: true}); // TOR9-20390 Force reload to re-render
        }
    }

    private async onTerritoryFilterChange() {
        await this.update({isCriteriaChanged: true});
    }

    private async onTerritoryLevelChange() {
        await this.update({});
    }

    private onSelectionChange(){
        const selected = this.getSelectedFeatureIds();
        const filter: ExpressionFilterSpecification = ['in', ['id'], ['literal', selected]];
        this._selectionLayer?.setLayerFilter(filter);
        this._selectionLayer?.update({});
    }

    private getSelectedFeatureIds(): number[] {
        const selected: number[] = [];
        const nodes = this._territoryModel.getPointMapping().layerPointMapping.get(this.layerView.layerMetaId);
        if (nodes) {
            for (const [e, n] of nodes.entries()) {
                const isSelected = this._territoryModel.getSelectionModel().isPointFeatureSelected(this.layerView.layerMetaId, e);
                if (isSelected) {
                    selected.push(n.featureId);
                }
            }
        }
        return selected
    }

    public get territoryModel(): TerritoryModel {
        return this._territoryModel
    }

    public getLayerIds(): string[] {
        const layerIds = [...super.getLayerIds()];
        if (this._selectionLayer) {
            const selectionLayerId = isBubbleLayer(this.layerView, this.layerInfo) ?
                this._selectionLayer.bubbleLayer.getLayerId() :
                this._selectionLayer.symbolLayer.getLayerId();
            layerIds.push(selectionLayerId);
        }
        return layerIds;
    }

    public override destroy(): void {
        //debugger;
        
        super.destroy();

        if (this._selectionLayer) {
            this._selectionLayer.destroy();
            this._selectionLayer = undefined;
        }

        if (this._selectionDs) {
            this.datasourceManager.remove(this._selectionDs.getId());
            this._selectionDs = undefined;
        }

        this._territoryModel.eventChannel
            .removeListener("onTerritoryChange", this.onTerritoryChange, this)
            .removeListener("onTerritoryFilterChange", this.onTerritoryFilterChange, this)
            .removeListener("onTerritoryLevelChange", this.onTerritoryLevelChange, this);

        this._territoryModel.getSelectionModel().getEventChannel()
            .removeListener("onSelectionChange", this.onSelectionChange, this);
    }
}
